import React from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import type { DashboardAppointmentItem } from '@/lib/api/dashboard';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

import appointmentDateIcon from '@/assets/images/appointments/appointment-date.svg';

import {
  CS_PRIMARY,
  DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT,
  DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX,
  DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX,
} from './dashboardHomeTokens';
import {
  formatDashboardDayNumber,
  formatDashboardMonthShort,
  formatDashboardTime,
  toDashboardTitle,
} from './dashboardHomeFormatters';
import {
  canJoinAppointment,
  DASHBOARD_JOIN_WINDOW_MINUTES,
  shouldHideAppointmentJoinRescheduleCancel,
} from '@/utils/appointments';

const COLLAPSED_APPOINTMENT_COUNT = 2;
const EXPANDED_APPOINTMENT_COUNT = 3;

const appointmentCardSx = {
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.03)',
  overflow: 'hidden',
};

const appointmentTypeLabel = (item: DashboardAppointmentItem) =>
  item.appointmentType ? toDashboardTitle(item.appointmentType) : 'Appointment';

interface DashboardAppointmentCardProps {
  item: DashboardAppointmentItem;
  onViewCalendar?: () => void;
}

const DashboardAppointmentCard: React.FC<DashboardAppointmentCardProps> = ({ item, onViewCalendar }) => {
  const startAt = item.startTime ? new Date(item.startTime) : null;
  const endAt = item.endTime ? new Date(item.endTime) : null;
  const durationMinutes =
    startAt && endAt && !Number.isNaN(startAt.getTime()) && !Number.isNaN(endAt.getTime())
      ? Math.max(1, Math.round((endAt.getTime() - startAt.getTime()) / 60_000))
      : null;
  const timeFallback =
    startAt && !Number.isNaN(startAt.getTime()) ? { startAt, durationMinutes } : undefined;

  const hideActions = shouldHideAppointmentJoinRescheduleCancel(
    item.status,
    item.endTime ?? null,
    timeFallback,
  );
  const showJoin = Boolean(
    item.meetingLink &&
      canJoinAppointment(item.status, item.startTime, item.endTime, timeFallback, {
        joinWindowMinutes: DASHBOARD_JOIN_WINDOW_MINUTES,
      }),
  );

  return (
    <Paper elevation={0} sx={appointmentCardSx}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          p: 1.5,
          borderLeft: `2px solid ${CS_PRIMARY}80`,
          borderRadius: '12px',
        }}
      >
        <Stack alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 500, color: CS_PRIMARY, lineHeight: 1 }}>
            {formatDashboardDayNumber(item.startTime)}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: CS_PRIMARY, letterSpacing: 1 }}>
            {formatDashboardMonthShort(item.startTime)}
          </Typography>
        </Stack>
        <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1e2939' }}>
              {item.title || appointmentTypeLabel(item)}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: '#000' }}>
              {formatDashboardTime(item.startTime)} · {appointmentTypeLabel(item)} ·{' '}
              {item.clientName ? (
                <ClientNameLink clientId={item.clientId} className="text-inherit hover:text-inherit">
                  {item.clientName}
                </ClientNameLink>
              ) : (
                'Client TBD'
              )}
            </Typography>
          </Box>
          {!hideActions && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {showJoin && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<VideocamRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => window.open(item.meetingLink || undefined, '_blank', 'noopener,noreferrer')}
                  sx={{
                    bgcolor: CS_PRIMARY,
                    borderRadius: '10px',
                    height: 34,
                    textTransform: 'none',
                    '&:hover': { bgcolor: CS_PRIMARY, opacity: 0.92 },
                  }}
                >
                  Join Now
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={onViewCalendar}
                sx={{
                  borderRadius: '10px',
                  height: 34,
                  borderColor: '#d1d5dc',
                  color: '#000',
                  textTransform: 'none',
                }}
              >
                Reschedule
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export interface DashboardUpcomingScheduleCardProps {
  items?: DashboardAppointmentItem[];
  appointmentsTodayCount?: number;
  isLoading?: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onViewAll?: () => void;
  onViewCalendar?: () => void;
}

export const DashboardUpcomingScheduleCard: React.FC<DashboardUpcomingScheduleCardProps> = ({
  items = [],
  appointmentsTodayCount = 0,
  isLoading = false,
  expanded,
  onToggleExpanded,
  onViewAll,
  onViewCalendar,
}) => {
  const canExpand = items.length > COLLAPSED_APPOINTMENT_COUNT;
  const isExpanded = expanded && canExpand;
  const visibleCount = !canExpand
    ? items.length
    : isExpanded
      ? EXPANDED_APPOINTMENT_COUNT
      : COLLAPSED_APPOINTMENT_COUNT;
  const visibleItems = items.slice(0, visibleCount);
  const isEmpty = !isLoading && visibleItems.length === 0;
  const emptyStateText = appointmentsTodayCount > 0
    ? 'No more appointments scheduled for today.'
    : 'No appointments scheduled for today.';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        ...(isEmpty ? { minHeight: DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT } : {}),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            component="img"
            src={appointmentDateIcon}
            alt=""
            aria-hidden
            sx={{ width: 26, height: 26, objectFit: 'contain', display: 'block' }}
          />
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e2939' }}>Today's Schedule</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            data-demo-id="dashboard-view-all"
            variant="text"
            size="small"
            onClick={onViewAll}
            endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: CS_PRIMARY,
              minWidth: 0,
            }}
          >
            View all
          </Button>
          {canExpand && (
            <IconButton
              size="small"
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
              onClick={onToggleExpanded}
              sx={{ color: '#000' }}
            >
              {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        sx={
          visibleItems.length > 0
            ? {}
            : {
                ...DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX,
              }
        }
      >
        {isLoading && items.length === 0 && (
          <Typography sx={{ fontSize: 12, color: '#4a5565', textAlign: 'center' }}>Loading appointments...</Typography>
        )}
        {!isLoading && visibleItems.length === 0 && (
          <Typography sx={DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX}>
            {emptyStateText}
          </Typography>
        )}
        {visibleItems.map((item) => (
          <DashboardAppointmentCard key={item.id} item={item} onViewCalendar={onViewCalendar} />
        ))}
      </Stack>
    </Paper>
  );
};
